import type { PropsWithChildren } from "react";

import { V2Heading, V2Paper } from "@stacklet/ui";
import { graphql, useFragment } from "react-relay";
import { Outlet } from "react-router";

import { BreadCrumbsHeader } from "app/components/BreadCrumbsHeader";
import { useTitle } from "app/hooks/ui/useTitle";

import type { PageWithBreadCrumbs_query$key } from "./__generated__/PageWithBreadCrumbs_query.graphql";
import type { BreadCrumbElement } from "app/components/BreadCrumbsHeader";

export interface PageWithBreadCrumbsProps extends PropsWithChildren {
  queryRef: PageWithBreadCrumbs_query$key;
  breadcrumbs?: BreadCrumbElement[];
  /** Prefix for the document/browser title, joined as `${defaultTitle} | ${title}`. */
  defaultTitle?: string;
  /** Page title: shown as the trailing breadcrumb and used to build the document title. */
  title?: string;
  /** Optional page heading (H2) rendered above the page content. */
  heading?: string;
  /** Constrain the paper's content to a fixed form width. */
  isForm?: boolean;
  /** Wrap content in a card-like Paper surface. */
  withPaper?: boolean;
}

export function PageWithBreadCrumbs({
  queryRef,
  defaultTitle,
  breadcrumbs,
  children,
  title = "",
  heading = "",
  isForm = false,
  withPaper = true,
}: PageWithBreadCrumbsProps) {
  const data = useFragment(
    graphql`
      fragment PageWithBreadCrumbs_query on Query {
        ...BreadCrumbsHeader_query
      }
    `,
    queryRef,
  );

  const documentTitle = defaultTitle ? `${defaultTitle} | ${title}` : title;
  useTitle(documentTitle);

  const content = (
    <>
      {heading ? (
        <V2Heading className="mb-xl" level={2}>
          {heading}
        </V2Heading>
      ) : null}
      {children}
    </>
  );

  return (
    <div className="grow">
      <Outlet />
      {breadcrumbs?.length ? (
        <BreadCrumbsHeader
          breadcrumbs={breadcrumbs}
          currentPageTitle={title}
          queryRef={data}
        />
      ) : null}

      {withPaper ? (
        <V2Paper>
          {isForm ? <div className="w-[700px]">{content}</div> : content}
        </V2Paper>
      ) : (
        content
      )}
    </div>
  );
}
